// src/state/pinia/project.js
import { defineStore } from "pinia";
import api, { isAuthError } from "@/api/api";
import { useAuthStore } from "@/state/pinia/auth";

function getStorageKey() {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const uuid = user?.uuid || 'default';
        return `activeProject_${uuid}`;
    } catch {
        return 'activeProject_default';
    }
}

export const useProjectStore = defineStore("project", {
    state: () => ({
        all: [],                       // lista projektów aktywnego workspace'a
        allGrouped: [],                // wszystkie projekty zgrupowane per workspace
        activeProject: localStorage.getItem(getStorageKey()) || null,
        activeProjectObj: null,

        // cache per-projekt
        collectionsByProject: {},
        pagesByProject: {},
        menusByProject: {},
        blocksByProject: {},
        elementsByProject: {},
        schemasByProject: {},
        formsByProject: {},
        domainsByProject: {},
        linkedCommerceByProject: {},      // linked LessCommerce project or null, keyed by CMS project code
        linkedSystemsByProject: {},       // array of linked sibling-system projects (commerce/seo), keyed by CMS project code
        shopPagesByProject: {},           // shop pages (cart, checkout, account, product, category) keyed by CMS project code

        // loadingi, by nie dublować strzałów
        loadingList: false,
        _loading: {
            collections: {},
            pages: {},
            menus: {},
            blocks: {},
            elements: {},
            schemas: {},
            forms: {},
            domains: {},
            linkedCommerce: {},
            linkedSystems: {},
            shopPages: {},
        },
    }),

    getters: {
        activeCollections: (s) => s.activeProject ? (s.collectionsByProject[s.activeProject] || []) : [],
        activePages:       (s) => s.activeProject ? (s.pagesByProject[s.activeProject] || []) : [],
        activeMenus:       (s) => s.activeProject ? (s.menusByProject[s.activeProject] || []) : [],
        activeBlocks:      (s) => s.activeProject ? (s.blocksByProject[s.activeProject] || []) : [],
        activeElements:    (s) => s.activeProject ? (s.elementsByProject[s.activeProject] || []) : [],
        activeSchemas:     (s) => s.activeProject ? (s.schemasByProject[s.activeProject] || []) : [],
        activeForms:       (s) => s.activeProject ? (s.formsByProject[s.activeProject] || []) : [],
        activeDomains:     (s) => s.activeProject ? (s.domainsByProject[s.activeProject] || []) : [],
        projectHasDomain:  (s) => s.activeProject ? (s.domainsByProject[s.activeProject] || []).length > 0 : false,
        activeLinkedCommerce: (s) => s.activeProject ? (s.linkedCommerceByProject[s.activeProject] || null) : null,
        hasLinkedCommerce: (s) => s.activeProject ? !!s.linkedCommerceByProject[s.activeProject] : false,
        activeLinkedSystems: (s) => s.activeProject ? (s.linkedSystemsByProject[s.activeProject] || []) : [],
        activeShopPages: (s) => s.activeProject ? (s.shopPagesByProject[s.activeProject] || []) : [],
    },

    actions: {
        // Reset all state (used on logout to prevent stale data)
        resetStore() {
            this.all = [];
            this.allGrouped = [];
            this.activeProject = null;
            this.activeProjectObj = null;
            this.collectionsByProject = {};
            this.pagesByProject = {};
            this.menusByProject = {};
            this.blocksByProject = {};
            this.elementsByProject = {};
            this.schemasByProject = {};
            this.formsByProject = {};
            this.domainsByProject = {};
            this.linkedCommerceByProject = {};
            this.linkedSystemsByProject = {};
            this.shopPagesByProject = {};
            this._loading = {
                collections: {},
                pages: {},
                menus: {},
                blocks: {},
                elements: {},
                schemas: {},
                forms: {},
                domains: {},
                linkedCommerce: {},
                shopPages: {},
            };
            localStorage.removeItem(getStorageKey());
            // Clean up legacy global key
            localStorage.removeItem('activeProject');
        },

        // 1) Pobranie listy projektów do selektora
        async fetchProjects() {
            this.loadingList = true;

            try {
                const authStore = useAuthStore();
                const workspaceUuid = authStore.getActiveWorkspace();
                const res = await api.get(`/api/projects`, {
                    params: workspaceUuid ? { workspace_uuid: workspaceUuid } : {},
                });
                const list = res?.data?.data ?? res?.data ?? [];

                // normalizacja pól
                this.all = Array.isArray(list)
                    ? list.map(p => ({
                        uuid: p.uuid ?? p.id ?? p.code,
                        code: p.code ?? p.uuid ?? p.id,
                        name: p.name ?? p.code ?? p.uuid,
                        ...p,
                    }))
                    : [];

                // sprawdź czy aktywny projekt jest w liście tego workspace'a
                const activeProjectInList = this.activeProject
                    ? this.all.find(p => p.code === this.activeProject || p.uuid === this.activeProject)
                    : null;

                if (activeProjectInList) {
                    // projekt jest w liście - ustaw obiekt
                    this.activeProjectObj = activeProjectInList;
                } else if (this.activeProject) {
                    // projekt nie jest w liście workspace'a - pobierz go z API
                    try {
                        const projectRes = await api.get(`/api/projects/${this.activeProject}`);
                        const projectData = projectRes?.data?.data ?? projectRes?.data;
                        if (projectData) {
                            const normalized = {
                                uuid: projectData.uuid ?? projectData.code,
                                code: projectData.code ?? projectData.uuid,
                                name: projectData.name ?? projectData.code,
                                ...projectData,
                            };
                            this.activeProjectObj = normalized;
                            // Switch workspace context to match project
                            if (projectData.workspace_uuid) {
                                const authStore = useAuthStore();
                                authStore.setActiveWorkspace(projectData.workspace_uuid);
                            }
                        } else {
                            this.setActive(null);
                        }
                    } catch {
                        this.setActive(null);
                    }
                }
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) console.warn("[projects] 403 forbidden – brak uprawnień / nieprzypisany do workspace");
                else console.error("[projects] fetchProjects error", e);
                this.all = [];
            } finally {
                this.loadingList = false;
            }
        },

        // 1b) Pobranie WSZYSTKICH projektów ze wszystkich workspace'ów (do switchera)
        async fetchAllProjects() {
            try {
                const res = await api.get(`/api/projects/all`);
                const list = res?.data?.data ?? [];
                const authStore = useAuthStore();
                const workspaces = authStore.getUser()?.workspaces || {};

                // Grupuj projekty per workspace
                const grouped = {};
                for (const p of list) {
                    const wsUuid = p.workspace_uuid;
                    if (!grouped[wsUuid]) {
                        grouped[wsUuid] = {
                            uuid: wsUuid,
                            name: authStore.getWorkspaceName(wsUuid) || wsUuid,
                            projects: [],
                        };
                    }
                    grouped[wsUuid].projects.push({
                        uuid: p.uuid ?? p.id ?? p.code,
                        code: p.code ?? p.uuid ?? p.id,
                        name: p.name ?? p.code ?? p.uuid,
                        workspace_uuid: wsUuid,
                        ...p,
                    });
                }

                // Dodaj workspace'y bez projektów
                for (const wsUuid of Object.keys(workspaces)) {
                    if (!grouped[wsUuid]) {
                        grouped[wsUuid] = {
                            uuid: wsUuid,
                            name: authStore.getWorkspaceName(wsUuid) || wsUuid,
                            projects: [],
                        };
                    }
                }

                // Sortuj: aktywny workspace na górę, potem alfabetycznie
                const activeWs = authStore.activeWorkspace;
                this.allGrouped = Object.values(grouped).sort((a, b) => {
                    if (a.uuid === activeWs) return -1;
                    if (b.uuid === activeWs) return 1;
                    return a.name.localeCompare(b.name);
                });
            } catch (e) {
                console.error("[projects] fetchAllProjects error", e);
                this.allGrouped = [];
            }
        },

        // Update a single key in activeProjectObj.settings (and mirror in `all`)
        updateActiveProjectSetting(key, value) {
            if (!this.activeProjectObj) return;
            const current = this.activeProjectObj.settings && typeof this.activeProjectObj.settings === 'object'
                ? { ...this.activeProjectObj.settings }
                : {};
            current[key] = value;
            this.activeProjectObj.settings = current;

            // Mirror in the `all` list entry if present
            const idx = this.all.findIndex(p => p.code === this.activeProjectObj.code || p.uuid === this.activeProjectObj.uuid);
            if (idx !== -1) {
                this.all[idx] = { ...this.all[idx], settings: current };
            }
        },

        // 2) Ustawienie aktywnego projektu + obiekt
        setActive(codeOrUuid) {
            this.activeProject = codeOrUuid || null;
            const key = getStorageKey();
            if (this.activeProject) localStorage.setItem(key, this.activeProject);
            else localStorage.removeItem(key);

            this.activeProjectObj =
                this.all.find(p => p.code === this.activeProject || p.uuid === this.activeProject) || null;
        },

        // 3) Ładowanie contentu aktywnego projektu hurtem (opcjonalnie użyj po wyborze)
        async fetchAllForActive() {
            const pid = this.activeProject;
            if (!pid) return;
            await Promise.allSettled([
                this.fetchCollections(pid),
                this.fetchPages(pid),
                this.fetchMenus(pid),
                this.fetchBlocks(pid),
                this.fetchElements(pid),
                this.fetchSchemas(pid),
                this.fetchForms(pid),
            ]);
        },

        // 4) Poniżej „czyściki” z loading-guardami i cache; routy per-workspace + projekt
        async fetchCollections(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.collectionsByProject[pid]) return;
            if (this._loading.collections[pid]) return;

            this._loading.collections[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/collections`);
                const collections = res?.data?.data ?? [];
                // Sort by sort_order, then by created_at
                collections.sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.collectionsByProject[pid] = collections;
            } catch (error) {
                // Silently ignore auth/authorization errors
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.collections[pid] = false;
            }
        },

        async fetchPages(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.pagesByProject[pid]) return;
            if (this._loading.pages[pid]) return;

            this._loading.pages[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/pages`);
                const pages = res?.data?.data ?? [];
                // Sort by sort_order, then by created_at
                pages.sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.pagesByProject[pid] = pages;
            } catch (error) {
                // Silently ignore auth/authorization errors
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.pages[pid] = false;
            }
        },

        async fetchMenus(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.menusByProject[pid]) return;
            if (this._loading.menus[pid]) return;

            this._loading.menus[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/menus`);
                const menus = res?.data?.data ?? [];
                // Sort by sort_order, then by created_at
                menus.sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.menusByProject[pid] = menus;
            } catch (error) {
                // Silently ignore auth/authorization errors
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.menus[pid] = false;
            }
        },

        async fetchBlocks(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.blocksByProject[pid]) return;
            if (this._loading.blocks[pid]) return;

            this._loading.blocks[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/blocks`);
                const blocks = res?.data?.data ?? [];
                // Sort by sort_order, then by created_at
                blocks.sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.blocksByProject[pid] = blocks;
            } catch (error) {
                // Silently ignore auth/authorization errors
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.blocks[pid] = false;
            }
        },

        async fetchElements(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.elementsByProject[pid]) return;
            if (this._loading.elements[pid]) return;

            this._loading.elements[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/elements`);
                const elements = res?.data?.data ?? [];
                // Sort by sort_order, then by created_at
                elements.sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.elementsByProject[pid] = elements;
            } catch (error) {
                // Silently ignore auth/authorization errors
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.elements[pid] = false;
            }
        },

        async fetchSchemas(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.schemasByProject[pid]) return;
            if (this._loading.schemas[pid]) return;

            this._loading.schemas[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/schemas`);
                const schemas = res?.data?.data ?? [];
                // Sort by created_at
                schemas.sort((a, b) => {
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.schemasByProject[pid] = schemas;
            } catch (error) {
                // Silently ignore auth/authorization errors
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.schemas[pid] = false;
            }
        },

        async fetchForms(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.formsByProject[pid]) return;
            if (this._loading.forms[pid]) return;

            this._loading.forms[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/forms`);
                const forms = res?.data?.data ?? [];
                forms.sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
                this.formsByProject[pid] = forms;
            } catch (error) {
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.forms[pid] = false;
            }
        },

        async fetchDomains(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.domainsByProject[pid]) return;
            if (this._loading.domains[pid]) return;

            this._loading.domains[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/domains`);
                this.domainsByProject[pid] = res?.data?.data ?? [];
            } catch (error) {
                if (!isAuthError(error)) {
                    throw error;
                }
            } finally {
                this._loading.domains[pid] = false;
            }
        },

        async fetchLinkedCommerce(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.linkedCommerceByProject[pid] !== undefined) return;
            if (this._loading.linkedCommerce[pid]) return;

            this._loading.linkedCommerce[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/linked-commerce`);
                this.linkedCommerceByProject[pid] = res?.data?.data || null;
            } catch (error) {
                if (!isAuthError(error)) {
                    this.linkedCommerceByProject[pid] = null;
                }
            } finally {
                this._loading.linkedCommerce[pid] = false;
            }
        },

        async fetchLinkedSystems(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.linkedSystemsByProject[pid] !== undefined) return;
            if (this._loading.linkedSystems[pid]) return;

            this._loading.linkedSystems[pid] = true;
            try {
                const res = await api.get(`/api/projects/${pid}/linked-systems`);
                this.linkedSystemsByProject[pid] = res?.data?.data ?? [];
            } catch (error) {
                if (!isAuthError(error)) {
                    this.linkedSystemsByProject[pid] = [];
                }
            } finally {
                this._loading.linkedSystems[pid] = false;
            }
        },

        async fetchShopPages(code, force = false) {
            const pid = code || this.activeProject;
            if (!pid) return;
            if (!force && this.shopPagesByProject[pid]) return;
            if (this._loading.shopPages[pid]) return;

            this._loading.shopPages[pid] = true;
            try {
                // Plain list — never auto-create. Used to call POST /shop/pages/ensure
                // which idempotently re-seeded everything (incl. per-template product
                // pages), so deleting a page bounced it right back on the next sidebar
                // mount. The user creates shop pages opt-in via the "Dodaj stronę
                // sklepu" modal now.
                const res = await api.get(`/api/projects/${pid}/shop/pages`);
                this.shopPagesByProject[pid] = res?.data?.data ?? [];
            } catch (error) {
                if (!isAuthError(error)) {
                    this.shopPagesByProject[pid] = [];
                }
            } finally {
                this._loading.shopPages[pid] = false;
            }
        },

        async fetchAvailableShopPages(code) {
            const pid = code || this.activeProject;
            if (!pid) return [];
            try {
                const res = await api.get(`/api/projects/${pid}/shop/pages/available`);
                return res?.data?.data ?? [];
            } catch (error) {
                return [];
            }
        },

        async createShopPage(code, ecommerceType, ecommerceTemplateCode = null) {
            const pid = code || this.activeProject;
            if (!pid || !ecommerceType) return null;
            const payload = { ecommerce_type: ecommerceType };
            if (ecommerceTemplateCode) {
                payload.ecommerce_template_code = ecommerceTemplateCode;
            }
            const res = await api.post(`/api/projects/${pid}/shop/pages`, payload);
            // Refresh the shop pages list so the new page appears in sidebar
            await this.fetchShopPages(pid, true);
            return res?.data?.data ?? null;
        },
    },
});
